import dayjs from 'dayjs';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { showErrorToast } from '@app/components/CommonToast';
import IfcTypeLabel from '@app/components/IfcTypeLabel';
import { loadingScreen } from '@app/components/Loading';
import StatusLabel from '@app/components/StatusLabel';
import { FILE_TYPE } from '@app/consts/common_consts';
import { SC007_CONST } from '@app/consts/component_consts';
import { MessageUtils } from '@app/modules/errors';
import { i18n } from '@app/modules/i18n-locales';
import { LANGUAGE_DEFAULT } from '@app/modules/i18n-locales/i18n';
import { screenInitApiService } from '@app/services/screenInitApi';
import { DatasetTemplateDocument, DatasetTemplateDocumentFile, DocumentFile, Publication } from '@app/types/dataSet';
import { SC007InitResponse } from '@app/types/sc007';
import { getStructureLabel, getStructureTypeValue } from '@app/utils/filterDataUtils';
import { getFileIcon } from '@app/utils/ImgUtils';
import downloadIcon from '@src/assets/imgs/icons/download.svg';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Tooltip } from 'primereact/tooltip';
import { useTranslation } from 'react-i18next';

import './dataTableHelper.scss';

export const customSortIcon = (option: any) => {
  if (option.sortOrder === -1) {
    return <i className="pi pi-angle-down text-color-primary" />;
  }
  if (option.sortOrder === 1) {
    return <i className="pi pi-angle-up text-color-primary" />;
  }
  return <i />;
};

export const EllipsisCell: React.FC<{ value: string }> = ({ value }) => {
  const spanRef = useRef<HTMLSpanElement>(null);
  const [isDisabled, setIsDisabled] = useState(true);

  const handleMouseEnter = useCallback(() => {
    const el = spanRef.current;
    if (el) {
      const containerWidth = el.getBoundingClientRect().width;
      let contentWidth = containerWidth;
      if (el.childNodes.length > 0) {
        const range = document.createRange();
        range.selectNodeContents(el);
        contentWidth = range.getBoundingClientRect().width;
      }
      const isOverflow = contentWidth > containerWidth;
      setIsDisabled(!isOverflow);
    }
  }, []);

  const uid = useRef(`et-${Math.random().toString(36).slice(2, 9)}`).current;

  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <span
        ref={spanRef}
        id={uid}
        className="cell-ellipsis"
        data-pr-tooltip={value}
        data-pr-disabled={isDisabled}
        data-pr-position="top"
        style={{
          display: 'block',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          width: '100%',
          minWidth: 0,
        }}
        onMouseEnter={handleMouseEnter}
      >
        {value.length > 0 ? value : '-'}
      </span>
      <Tooltip target={`#${uid}`} className="custom-tooltip data-table-tooltip" />
    </>
  );
};

/**
 * EllipsisText — inline text that truncates with CSS ellipsis and shows a PrimeReact tooltip on hover.
 *
 * Props:
 *  - children    : Content to display. When `tooltipText` is omitted, the text value of `children`
 *                  (string or number only) is used automatically as the tooltip.
 *  - tooltipText : Override the tooltip content.
 *                    • omitted / undefined  → tooltip text = children text (auto)
 *                    • string               → tooltip shows that string; if it contains '\n' the
 *                                             component displays only the first line + '…' and the
 *                                             tooltip is always visible (not just on overflow)
 *                    • null                 → tooltip is disabled entirely (no Tooltip rendered)
 *  - className   : Extra CSS class added to the wrapper <span>.
 *
 * @example
 * // Auto tooltip from children
 * <EllipsisText>{rowData.name}</EllipsisText>
 *
 * // Custom tooltip text
 * <EllipsisText tooltipText={`${rowData.name}\n${rowData.code}`}>{rowData.name}</EllipsisText>
 *
 * // No tooltip
 * <EllipsisText tooltipText={null}>{rowData.name}</EllipsisText>
 */
/**
 * EllipsisText — inline text that truncates with CSS ellipsis and shows a PrimeReact tooltip on hover.
 *
 * Props:
 *  - children    : Content to display. When `tooltipText` is omitted, the text value of `children`
 *                  (string or number only) is used automatically as the tooltip.
 *  - tooltipText : Override the tooltip content.
 *                    • omitted / undefined  → tooltip text = children text (auto)
 *                    • string               → tooltip shows that string; if it contains '\n' the
 *                                             component displays only the first line + '…' and the
 *                                             tooltip is always visible (not just on overflow)
 *                    • null                 → tooltip is disabled entirely (no Tooltip rendered)
 *  - className   : Extra CSS class added to the wrapper <span>.
 *  - maxLines    : Maximum lines before truncating with ellipsis. Defaults to 1 (single-line).
 *                  When > 1, uses CSS -webkit-line-clamp for multi-line truncation with tooltip on overflow.
 *
 * @example
 * // Auto tooltip from children (single line)
 * <EllipsisText>{rowData.name}</EllipsisText>
 *
 * // Multi-line (2 lines max)
 * <EllipsisText maxLines={2}>{rowData.description}</EllipsisText>
 *
 * // Custom tooltip text
 * <EllipsisText tooltipText={`${rowData.name}\n${rowData.code}`}>{rowData.name}</EllipsisText>
 *
 * // No tooltip
 * <EllipsisText tooltipText={null}>{rowData.name}</EllipsisText>
 */
export const EllipsisText: React.FC<{
  children: React.ReactNode;
  tooltipText?: string | null;
  className?: string;
  /** Maximum lines to display before truncating with ellipsis. Defaults to 1. */
  maxLines?: number;
}> = ({ children, tooltipText, className, maxLines = 1 }) => {
  const spanRef = useRef<HTMLSpanElement>(null);
  const uid = useRef(`et-${Math.random().toString(36).slice(2, 9)}`).current;
  const isMultiLine = maxLines > 1;
  const [isWrapped, setIsWrapped] = useState(false);

  // tooltipText === null → no tooltip; undefined → fall back to children text
  const noTooltip = tooltipText === null;
  const childrenText = typeof children === 'string' || typeof children === 'number' ? String(children) : '';
  const effectiveTooltip = noTooltip ? undefined : (tooltipText ?? childrenText);

  // When tooltipText has newlines: show only first line + ellipsis, tooltip always enabled
  const hasNewline = !isMultiLine && typeof effectiveTooltip === 'string' && effectiveTooltip.includes('\n');
  const firstLine = hasNewline ? `${effectiveTooltip!.split('\n')[0]}…` : undefined;

  useEffect(() => {
    const el = spanRef.current;
    if (!el) return;

    if (noTooltip) {
      el.setAttribute('data-pr-disabled', 'true');
      return;
    }

    el.setAttribute('data-pr-position', 'top');
    el.setAttribute('data-pr-tooltip', effectiveTooltip || '-');

    if (isMultiLine) {
      // Detect if text visually wraps (clientHeight > single line height) → force text-align: left
      // so -webkit-line-clamp ellipsis renders correctly regardless of inherited text-align.
      const checkOverflow = () => {
        const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || el.clientHeight;
        setIsWrapped(el.scrollHeight > lineHeight + 1);
        el.setAttribute('data-pr-disabled', String(el.scrollHeight <= el.clientHeight + 1));
      };
      checkOverflow();
      const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(checkOverflow) : null;
      ro?.observe(el);
      return () => ro?.disconnect();
    }

    if (hasNewline) {
      // Always show tooltip since content is truncated
      el.setAttribute('data-pr-disabled', 'false');
      return;
    }
    const handleMouseEnter = () => {
      // scrollWidth is unreliable when overflow:hidden (especially CJK text in Chromium).
      // Use Range API to measure the actual rendered text width instead.
      const containerWidth = el.getBoundingClientRect().width;
      let contentWidth = containerWidth;
      if (el.childNodes.length > 0) {
        const range = document.createRange();
        range.selectNodeContents(el);
        contentWidth = range.getBoundingClientRect().width;
      }
      const isOverflow = contentWidth > containerWidth;
      el.setAttribute('data-pr-disabled', String(!isOverflow));
    };
    el.addEventListener('mouseenter', handleMouseEnter);
    return () => el.removeEventListener('mouseenter', handleMouseEnter);
  }, [effectiveTooltip, children, hasNewline, noTooltip, isMultiLine]);

  const singleLineStyle: React.CSSProperties = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'block',
  };

  return (
    <>
      <span
        ref={spanRef}
        id={uid}
        className={`ellipsis-text${isMultiLine ? ' ellipsis-text--multiline' : ''}${className ? ` ${className}` : ''}`}
        style={isMultiLine ? ({ '--ellipsis-lines': maxLines, ...(isWrapped && { textAlign: 'left' }) } as React.CSSProperties) : singleLineStyle}
      >
        {hasNewline ? firstLine : children || '-'}
      </span>
      {!noTooltip && <Tooltip target={`#${uid}`} className="custom-tooltip data-table-tooltip" />}
    </>
  );
};

// Ellipsis cell template with overflow tooltip
export const ellipsisCellTemplate = (field: keyof any) => (rowData: any) => {
  const value = String(rowData[field] || '-');
  return <EllipsisText>{value}</EllipsisText>;
};
// Ellipsis string template with overflow tooltip (for none table fields)
export const ellipsisStringTemplate = (strValue: string) => () => {
  const value = String(strValue || '-');
  return <EllipsisCell value={value} />;
};

// Default cell template with fallback to '-'
export const defaultCellTemplate = (field: string) => (rowData: any) => {
  const value = rowData[field];
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'object') return value.label || value.value || JSON.stringify(value);
  return String(value);
};

// Projects cell - displays member roles with tooltip when multiple members exist
const ProjectsCell: React.FC<{ members: any[] }> = ({ members }) => {
  const { i18n } = useTranslation();
  const isJa = i18n.language === 'ja';

  const getName = (m: any) => {
    const roleName = isJa ? m.roleNameJp : m.roleNameEn;
    // Remove content within parentheses including the parentheses
    const cleanedRole = roleName ? roleName.replace(/\s*\([^)]*\)/g, '') : '';
    return m.projectName + ' (' + cleanedRole + ')';
  };

  if (!members || members.length === 0) return <>-</>;

  const firstName = getName(members[0]);
  const allNames = members.map(getName).join('\n');

  return (
    <span className="cell-members-tooltip" data-pr-tooltip={allNames} data-pr-disabled={false} data-pr-position="top">
      {firstName}...
    </span>
  );
};

export const projectsCellTemplate = (field: keyof any) => (rowData: any) => {
  const members = rowData[field];
  if (!Array.isArray(members)) return '-';
  return <ProjectsCell members={members} />;
};

// Dataset cell - displays dataset names with tooltip when multiple datasets exist
const DataSetCell: React.FC<{ datasets: string }> = ({ datasets }) => {
  const uid = useRef(`ds-${Math.random().toString(36).slice(2, 9)}`).current;
  if (!datasets || datasets.length === 0) return <>-</>;
  const getDatasetName = datasets.split(',').map((d) => d.trim());
  const displayText = getDatasetName.join(', ');
  const allNames = getDatasetName.join(', \n');

  return (
    <>
      <span
        id={uid}
        className="cell-dataset-tooltip"
        data-pr-tooltip={allNames}
        data-pr-disabled={false}
        data-pr-position="top"
        style={{
          display: 'block',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {displayText}
      </span>
      <Tooltip target={`#${uid}`} className="custom-tooltip data-table-tooltip" />
    </>
  );
};

export const dataSetCellTemplate = (field: keyof any) => (rowData: any) => {
  const datasets = rowData[field];
  if (!datasets || datasets === '') return '-';
  return <DataSetCell datasets={datasets} />;
};

// Date cell template - parses date/datetime string already in UTC+9 and displays as YYYY/MM/DD using system locale
export const dateCellTemplate = (field: keyof any) => (rowData: any) => {
  if (!rowData || !rowData[field]) {
    return '-';
  }
  const raw = rowData[field];
  const date = dayjs(raw);
  if (!date.isValid()) return '-';
  const locale = navigator.language;
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const parts = new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone, // Use user's local timezone for display
  }).formatToParts(date.toDate());
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '00';
  return `${get('year')}/${get('month')}/${get('day')}`;
};

/**
 * Creates a paginator template with an ellipsis (...) indicator appended after
 * the last visible page link when there are more pages beyond the visible range.
 * @param pageLinkSize - max number of page links shown at once (default 5)
 */
export const createPaginatorTemplate = (pageLinkSize = 5) => ({
  layout: 'PrevPageLink PageLinks NextPageLink',
  PageLinks: (options: any) => {
    const isLastVisible = options.page === options.view.endPage;
    const hasMore = options.totalPages > options.view.endPage + 1;
    return (
      <React.Fragment key={options.page}>
        {options.element}
        {isLastVisible && hasMore && (
          <span className="p-paginator-page" style={{ cursor: 'default', pointerEvents: 'none', opacity: 0.5 }}>
            ...
          </span>
        )}
      </React.Fragment>
    );
  },
});

// Custom cell for ifc type
export const roleBodyTemplate = (field: string) => (rowData: any) => {
  if (rowData.role) {
    const role = rowData.role;
    return <IfcTypeLabel ifcType={role.value} />;
  } else {
    return <IfcTypeLabel ifcType={getStructureTypeValue(rowData[field])} />;
  }
};

// Revision cell template: append '.0' to numeric/string revision values
export const revisionCellTemplate = (field: string) => (rowData: any) => {
  if (rowData?.[field] === null || rowData[field] === undefined || rowData[field] === '' || rowData[field] === '-')
    return '-';
  if (typeof rowData[field] === 'object')
    return rowData[field]?.label || rowData[field]?.value || JSON.stringify(rowData[field]);
  const value = rowData[field];
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'object') return value?.label || value?.value || JSON.stringify(value);
  // Ensure string and append .0 if not already contains a decimal
  const str = String(value);
  return str.includes('.') ? str : `${str}.0`;
};

export const documentCellTemplate = (field: string) => (rowData: any) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(0); // Default expand first tab
  const publication = rowData as Publication;
  const documents = rowData[field] ?? [];
  if (!Array.isArray(documents) || documents.length === 0) {
    return null;
  }
  return (
    <Accordion
      className="doc-accordion"
      activeIndex={activeIndex}
      onTabChange={(e) => setActiveIndex(e.index as number)}
    >
      <AccordionTab
        key={publication.publicationGUID}
        header={i18n.language === LANGUAGE_DEFAULT ? publication.professionNameJa : publication.professionNameEn}
      >
        {publication.documentFiles && (
          <ul className="list-none m-0 p-0">
            {publication.documentFiles && Array.isArray(publication.documentFiles)
              ? publication.documentFiles.map((file: any) => (
                  <li key={file.documentName} className="flex align-items-center py-1 file-item">
                    {getFileIcon(file.extensionFile)}
                    <span className="doc-name">
                      {file.documentName}.{file.extensionFile}
                    </span>
                  </li>
                ))
              : null}
          </ul>
        )}
        {publication.document && (
          <ul className="list-none m-0 p-0">
            {publication.document && Array.isArray(publication.document)
              ? publication.document.map((file: any) => (
                  <li key={file.documentName} className="flex align-items-center py-1 file-item">
                    {getFileIcon(file.extensionFile)}
                    <span className="doc-name">
                      {file.documentName}.{file.extensionFile}
                    </span>
                  </li>
                ))
              : null}
          </ul>
        )}
      </AccordionTab>
    </Accordion>
  );
};

/** DD §5.8.2 (v1.3) — Build display name from documentName + extensionFile.
 *  If documentName already ends with the extension, do not append again. */
const buildDocumentDisplayName = (documentName: string, extensionFile: string): string => {
  if (!extensionFile) return documentName;
  const ext = extensionFile.toLowerCase().replace(/^\./, '');
  if (documentName.toLowerCase().endsWith(`.${ext}`)) return documentName;
  return `${documentName}.${extensionFile}`;
};

/** DD §5.8.3 — Call API-COM-003 GET /api/documents/{boxFileId}/download then handle result.
 *  mode 'download': save file with downloadFileName; mode 'open': open URL in new tab. */
const handleDocumentAction = async (file: DocumentFile, mode: 'download' | 'open') => {
  try {
    loadingScreen.show();
    const res = await screenInitApiService.downloadFile(file.boxFileId ?? '');
    const url = res.data.content;
    if (!url) return;
    if (mode === 'open') {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      // DD §5.8.4 (v1.3) — file saved as {documentName}.{extension}
      const downloadFileName = buildDocumentDisplayName(file.documentName, file.extension);
      const link = document.createElement('a');
      link.href = url;
      link.download = downloadFileName;
      link.click();
    }
  } catch (error: any) {
    showErrorToast(MessageUtils.formatApiMessage(error) || i18n.t(error?.code, { ns: 'messages' }));
  } finally {
    loadingScreen.hide();
  }
};

export const DocumentArrayCell: React.FC<{ allDocuments: DatasetTemplateDocument[]; isViewFiles?: boolean }> = ({
  allDocuments,
  isViewFiles = true,
}) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  // Subscribe to i18n language changes so accordion headers re-render on language switch
  useTranslation();

  if (!Array.isArray(allDocuments) || allDocuments.length === 0) {
    return <span>-</span>;
  }

  const renderFile = (file?: DocumentFile, index: number = 0) => {
    // DD §5.8.2 (v1.3) — display name: do not double-append extension
    const displayName = buildDocumentDisplayName(file?.documentName ?? '', file?.extension ?? '');
    // DD §4.3 item 45 (v1.3) — disable download button when boxFileId is null/empty
    const downloadDisabled = !file?.boxFileId;
    return (
      <li key={`${file?.documentName}-${index}`} className="file-item">
        {getFileIcon(file?.extension as FILE_TYPE)}
        <span className="doc-name">
          <div>
            {/* DD §4.3 Version 1.9 / Ticket TNKBIM-8187 — link opens URL in new tab */}
            {isViewFiles ? (
              <a
                href={!downloadDisabled ? SC007_CONST.URL_BOX_FILE_ID(file!.boxFileId!) : '#'}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => {
                  if (downloadDisabled) e.preventDefault();
                }}
              >
                <EllipsisCell value={displayName} />
              </a>
            ) : (
              <EllipsisCell value={displayName} />
            )}
          </div>
          {/* DD §4.3 item 45 (v1.3) — btnDocumentDownload: disabled when boxFileId null */}
          <button
            className="p-button-text download-btn"
            onClick={() => handleDocumentAction(file!, 'download')}
            title="Download"
            disabled={downloadDisabled}
          >
            <span className="text-color-semantic-info p-button-icon p-c pi pi-download"></span>
          </button>
        </span>
      </li>
    );
  };

  const renderAccordionTab = (doc: DatasetTemplateDocument, index: number) => (
    <AccordionTab key={index} header={getStructureLabel(doc.role.value)} disabled={doc.isDisabled}>
      {doc.files.length > 0 && (
        <ul className="file-list list-none m-0 p-0">
          {doc.files.map((file: DatasetTemplateDocumentFile, idx: number) => renderFile(file, idx))}
        </ul>
      )}
    </AccordionTab>
  );

  return (
    <Accordion
      className="doc-accordion"
      activeIndex={activeIndex}
      onTabChange={(e) => setActiveIndex(e.index as number)}
    >
      {allDocuments.map((doc: DatasetTemplateDocument, index: number) => renderAccordionTab(doc, index))}
    </Accordion>
  );
};

export const documentArrayTemplate =
  (allDocuments: DatasetTemplateDocument[], isViewFiles: boolean = true) =>
  () => {
    return <DocumentArrayCell allDocuments={allDocuments} isViewFiles={isViewFiles} />;
  };

export const statusBodyTemplate = (rowData: any) =>
  rowData.statusCode ? <StatusLabel status={rowData.statusCode} /> : '-';

/**
 * Resolves FILE_TYPE from API response fields.
 * Handles all formats:
 *   - raw extension:  'pdf', '.pdf', 'DWG'
 *   - MIME type:      'application/pdf', 'image/vnd.dwg', 'application/vnd.ms-excel'
 *   - filename only:  'S-Frame.dwg', 'A-1F-Plan.pdf', 'EstimateLinkSheet.xlsx'
 */
export const resolveFileTypeFromDocument = (documentType: string, documentName: string): FILE_TYPE => {
  const fileTypeValues = Object.values(FILE_TYPE) as string[];

  // 1. Strip leading dot, lowercase (e.g. '.DWG' → 'dwg', 'pdf' → 'pdf')
  const rawType = documentType.toLowerCase().replace(/^\./, '');
  if (rawType && fileTypeValues.includes(rawType)) return rawType as FILE_TYPE;

  // 2. MIME type: extract subtype (e.g. 'application/pdf' → 'pdf', 'image/vnd.dwg' → 'dwg')
  if (rawType.includes('/')) {
    const subtype = rawType.split('/').pop() ?? '';
    // strip vnd. prefix and take first segment before any [-+.]
    const mimeExt = subtype.replace(/^vnd\./i, '').split(/[-+.]/)[0];
    if (mimeExt && fileTypeValues.includes(mimeExt)) return mimeExt as FILE_TYPE;
  }

  // 3. Fallback: last extension from documentName (handles 'S-Frame.dwg', 'A.1F.Plan.pdf')
  const nameExt = documentName.split('.').pop()?.toLowerCase() ?? '';
  if (nameExt && fileTypeValues.includes(nameExt)) return nameExt as FILE_TYPE;

  return FILE_TYPE.OTHER;
};

/**
 * DocumentNameCell - Document name + Download icon
 * Click on document name to navigate
 */
export const DocumentNameCell: React.FC<{
  rowData?: SC007InitResponse;
  onDownload?: (rowData: SC007InitResponse) => void;
  onDocumentClick?: (rowData: SC007InitResponse) => void;
  isShowDownloadIcon?: boolean;
  isShowTooltip?: boolean;
  isShowIconFile?: boolean;
}> = ({
  rowData,
  onDownload,
  onDocumentClick,
  isShowDownloadIcon = true,
  isShowTooltip = false,
  isShowIconFile = true,
}) => {
  const fileType = resolveFileTypeFromDocument(rowData?.fileExtension ?? '', rowData?.documentName ?? '');
  const handleDownloadClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDownload?.(rowData as SC007InitResponse);
  };
  const handleDocumentClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDocumentClick?.(rowData as SC007InitResponse);
  };
  const docNameId = `doc-name-${rowData?.documentId ?? rowData?.boxFileId ?? Math.random().toString(36).slice(2, 10)}`;

  // Tooltip only when text is truncated
  const [showTooltip, setShowTooltip] = useState(false);
  const textRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const checkOverflow = () => {
      const el = textRef.current;
      if (!el) return;

      // Basic overflow detection
      const basicOverflow = el.scrollWidth > el.clientWidth;

      // Enhanced detection: element's right edge overlapping reserved action area (if any)
      let advancedOverflow = false;
      const elRect = el.getBoundingClientRect();
      const td = el.closest('td') as HTMLElement | null;
      if (td) {
        const rightActions = td.querySelector<HTMLElement>('.right-actions');
        if (rightActions) {
          const rightActionsRect = rightActions.getBoundingClientRect();
          advancedOverflow = elRect.right > rightActionsRect.left - 2;
        } else {
          const tdRect = td.getBoundingClientRect();
          advancedOverflow = elRect.right > tdRect.right - 2;
        }
      }

      setShowTooltip(basicOverflow || advancedOverflow);
    };

    // Run after short delay to allow layout/DOM to settle (helps with grouped rows)
    const id = globalThis.setTimeout(checkOverflow, 0);
    globalThis.addEventListener('resize', checkOverflow);
    return () => {
      globalThis.clearTimeout(id);
      globalThis.removeEventListener('resize', checkOverflow);
    };
  }, [rowData?.documentName, rowData?.fileExtension]);

  // Determine file type for icon display
  const docText = rowData?.fileExtension ? `${rowData?.documentName}.${rowData?.fileExtension}` : rowData?.documentName;

  return (
    <div className="flex align-items-center" style={{ gap: '8px' }}>
      {isShowIconFile && <span className="flex-shrink-0">{getFileIcon(fileType)}</span>}
      {onDocumentClick ? (
        <>
          <button
            id={docNameId}
            ref={textRef as React.RefObject<HTMLButtonElement>}
            className="cell-ellipsis flex-1"
            style={{
              fontSize: '14px',
              fontWeight: 400,
              lineHeight: '18px',
              cursor: 'pointer',
              color: '#0066cc',
              textDecoration: 'underline',
              background: 'none',
              border: 'none',
              padding: 0,
              textAlign: 'left',
            }}
            onClick={handleDocumentClick}
          >
            {docText}
          </button>
          {isShowTooltip && showTooltip && (
            <Tooltip
              target={`#${docNameId}`}
              content={docText}
              className="custom-tooltip data-table-tooltip"
              position="top"
            />
          )}
        </>
      ) : (
        <>
          <span
            id={docNameId}
            ref={textRef}
            className="cell-ellipsis flex-1"
            style={{
              fontSize: '14px',
              fontWeight: 400,
              lineHeight: '18px',
            }}
          >
            {docText}
          </span>
          {isShowTooltip && showTooltip && (
            <Tooltip
              target={`#${docNameId}`}
              content={docText}
              className="custom-tooltip data-table-tooltip"
              position="top"
            />
          )}
        </>
      )}
      {isShowDownloadIcon && (
        <button
          className="p-button-text download-btn flex-shrink-0"
          style={{
            width: '16px',
            height: '16px',
            padding: 0,
            opacity: onDownload ? 1 : 0.3,
            cursor: onDownload ? 'pointer' : 'default',
            border: 'none',
          }}
          onClick={handleDownloadClick}
          title="Download"
          disabled={!onDownload}
        >
          <img src={downloadIcon} alt="download" width={16} height={16} />
        </button>
      )}
    </div>
  );
};

/**
 * [DD-SC007] § 1.2: documentNameCellTemplate - Document name cell
 * Click to navigate to SC004 (Dashboard)
 */
export const documentNameCellTemplate =
  (
    onDownload?: (rowData: any) => void,
    onDocumentClick?: (rowData: any) => void,
    isShowDownloadIcon?: boolean,
    isShowTooltip?: boolean
  ) =>
  (rowData: any) => (
    <DocumentNameCell
      rowData={rowData}
      onDownload={onDownload}
      onDocumentClick={onDocumentClick}
      isShowDownloadIcon={isShowDownloadIcon}
      isShowTooltip={isShowTooltip}
    />
  );
